"use client";

import { Button, Dialog, RenderIf, Typography, useMediaQuery } from "@repo/ui";
import { Clock, Download, Users } from "lucide-react";
import React from "react";
import DownloadBrochureForm from "../forms/DownloadBrochureForm";
import ImagePlaceholder from "../common/ImagePlaceholder";
import ShimmerImage from "../common/ShimmerImage";
import Link from "next/link";
import { useParams } from "next/navigation";
import { formatCurrency } from "@/lib/helpers";
import { ImageProps } from "next/image";

interface CourseCard2Props {
  tag?: string;
  title: string;
  coverSrc?: string;
  totalEnroll?: string;
  duration?: number | string;
  slug?: string;
  accreditation_name?: string;
  icon?: string;
  serviceId?: number; // Assuming serviceId is needed for the DownloadBrochureForm
  modeOfTraining?: string; // Optional, if needed for future use
  sellingPriceNumber?: number;
  currency?: string;
  imageLoading?: ImageProps["loading"];
}

const CourseCard2: React.FC<CourseCard2Props> = ({
  tag,
  title,
  coverSrc,
  totalEnroll,
  duration,
  slug,
  accreditation_name,
  icon,
  serviceId, // Assuming serviceId is needed for the DownloadBrochureForm
  modeOfTraining = "Live Classroom / Classroom", // Default value if not provided
  sellingPriceNumber,
  currency,
  imageLoading,
}) => {
  const { country } = useParams<{ country?: string }>();
  const sm = useMediaQuery("sm");
  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200  sm:max-w-sm  w-full relative overflow-hidden">
      <RenderIf condition={Boolean(tag)}>
        <span className="absolute top-0 left-0 bg-secondary-light text-primary-main text-xs font-semi-bold tracking-wide px-3 py-1 rounded-br-xl shadow-md">
          {tag}
        </span>
      </RenderIf>
      {/* Image and Badge */}
      <div className="p-4 pb-0">
        <RenderIf
          condition={Boolean(coverSrc)}
          fallback={<ImagePlaceholder size={145} />}
        >
          <ShimmerImage
            src={coverSrc || "/placeholder-image.png"}
            width={327}
            height={143}
            quality={75}
            loading={imageLoading}
            alt={title}
            className="w-full h-auto  object-cover rounded-xl aspect-[73/32]"
          />
        </RenderIf>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            {/* Accreditation */}
            <RenderIf condition={Boolean(accreditation_name)}>
              <Typography
                variant="caption"
                className="!text-xs text-gray-500 font-regular mb-2 block"
              >
                {accreditation_name}
              </Typography>
            </RenderIf>

            {/* Course Type (static for now) */}
            <RenderIf condition={Boolean(modeOfTraining)}>
              <Typography
                variant="caption"
                className="text-slate-500 !text-xs mb-2"
              >
                {modeOfTraining}
              </Typography>
            </RenderIf>
          </div>
          <div className="size-12 relative">
            <RenderIf
              condition={
                Boolean(icon) &&
                typeof icon === "string" &&
                icon.startsWith("https://")
              }
            >
              <ShimmerImage
                src={icon || ""}
                quality={90}
                alt={title + "icon"}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                className="w-auto h-auto object-contain"
                shimmerWidth={48}
                shimmerHeight={48}
              />
            </RenderIf>
          </div>
        </div>
        {/* Course Title */}
        <Typography
          gutterBottom
          variant="h6"
          render={<div />}
          className="truncate !font-bold"
        >
          {title}
        </Typography>

        {/* Info Row */}
        <div className="flex  items-center justify-between mb-4">
          <div className="flex flex-col  space-y-3">
            <div className="flex items-center text-gray-600 text-xs">
              {/* Clock Icon */}
              <Clock size={14} className="mr-2" />
              <Typography variant="caption">
                {duration ? `${duration}` : "-"}
              </Typography>
            </div>
            <div className="flex  text-gray-600 text-xs">
              {/* Users Icon */}
              <Users size={14} className="mr-2" />
              <Typography variant="caption">
                {totalEnroll ? totalEnroll.toLocaleString() : "-"} Enrolled
              </Typography>
            </div>
          </div>
          {sellingPriceNumber && currency && (
            <div className="text-right">
              <Typography
                variant="caption"
                render={<div />}
                className="!text-xs text-slate-500"
              >
                Start from
              </Typography>
              <Typography
                render={<div />}
                className="text-green-600 !text-lg font-bold"
              >
                {formatCurrency(sellingPriceNumber, currency)}
              </Typography>
            </div>
          )}
        </div>
        <hr className="mb-4" />
        {/* Buttons */}
        <div className="grid grid-flow-col gap-4">
          <Button
            variant="outlined"
            color="primary"
            size={sm ? "small" : "medium"}
            render={
              <Link
                href={
                  country ? `/${country}/course/${slug}` : `/course/${slug}`
                }
              />
            }
          >
            View Certification
          </Button>

          <Dialog
            className="!p-0"
            triggerRender={
              <Button
                color="primary"
                size={sm ? "small" : "medium"}
                startIcon={<Download size={16} />}
              />
            }
            triggerText="Curriculum"
            maxWidth="md"
          >
            <DownloadBrochureForm
              serviceId={serviceId}
              formType="COURSE_DOWNLOAD_BROCHURE"
              heading="Download Curriculum"
            />
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default CourseCard2;
